import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';

interface DocumentMeta {
  title: string;
  description: string;
  sourceFile: string;
  isCode?: boolean;
  ogImage?: string;
  ogDescription?: string;
}

const documents: Record<string, DocumentMeta> = {
  'technical-paper': {
    title: 'Cortex: Intelligence That Compounds',
    description: 'Technical Paper (6,388 words)',
    sourceFile: '/content/cortex-for-ai-operators.md',
    ogImage: '/assets/cortex/architecture-diagram.png',
    ogDescription: 'An intelligence layer that learns from your AI tool outcomes. Tracks experiments, calibrates confidence, and suggests next steps based on what actually worked.'
  },
  'summary': {
    title: 'Cortex: Executive Summary',
    description: 'Quick Overview (2,500 words)',
    sourceFile: '/content/cortex-executive-summary.md',
    ogImage: '/assets/cortex/og-image.png',
    ogDescription: 'Intelligence that compounds. Stop repeating failed AI experiments. Cortex tracks outcomes and calibrates confidence based on historical success rates.'
  },
  'blog': {
    title: 'Cortex: Intelligence That Compounds',
    description: 'Blog Post for Technical Readers',
    sourceFile: '/content/cortex-blog-post.md',
    ogImage: '/assets/cortex/compounding-effect.png',
    ogDescription: 'Your AI tools don\'t remember what worked yesterday. Here\'s the intelligence layer pattern that fixes it.'
  },
  'code': {
    title: 'Cortex: 100-Line Implementation',
    description: 'Minimal Learning System',
    sourceFile: '/content/cortex_minimal_learning.py',
    isCode: true,
    ogImage: '/assets/cortex/terminal-output.png',
    ogDescription: 'The entire learning system in 100 lines of Python. Tracks experiments, learns from outcomes, suggests next steps.'
  }
};

export const DocumentViewer = () => {
  const { docType } = useParams<{ docType: string }>();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const doc = docType ? documents[docType] : undefined;

  // Update meta tags for SEO and social sharing
  useDocumentMeta({
    title: doc?.title || 'Cortex - Document Not Found',
    description: doc?.ogDescription || doc?.description || 'Cortex: Intelligence that compounds',
    ogImage: doc?.ogImage,
    url: `https://kempion.com/cortex/${docType || ''}`
  });

  useEffect(() => {
    if (!doc) {
      setLoading(false);
      return;
    }

    fetch(doc.sourceFile)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(text => {
        setContent(text);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load document:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [doc]);

  if (!doc) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <h1 className="text-4xl font-bold mb-4">Document Not Found</h1>
          <p className="text-white/60 mb-6">
            The document you're looking for doesn't exist.
          </p>
          <Link to="/cortex" className="text-[#00d4ff] hover:underline">
            ← Back to Cortex
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="text-xl animate-pulse">Loading {doc.title}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <h1 className="text-4xl font-bold mb-4 text-red-500">Error Loading Document</h1>
          <p className="text-white/60 mb-6">
            {error}
          </p>
          <Link to="/cortex" className="text-[#00d4ff] hover:underline">
            ← Back to Cortex
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/cortex" className="text-[#00d4ff] hover:underline text-sm mb-2 inline-block">
            ← Back to Interactive Demo
          </Link>
          <h1 className="text-3xl font-bold">{doc.title}</h1>
          <p className="text-white/60 mt-1">{doc.description}</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {doc.isCode ? (
          <pre className="bg-black/50 p-6 rounded-lg overflow-x-auto border border-white/10">
            <code className="text-sm font-mono text-white/90">{content}</code>
          </pre>
        ) : (
          <article className="prose prose-invert prose-lg max-w-none
            prose-headings:font-bold prose-headings:text-white
            prose-h1:text-4xl prose-h1:mb-4 prose-h1:mt-8
            prose-h2:text-3xl prose-h2:mb-3 prose-h2:mt-6 prose-h2:border-b prose-h2:border-white/10 prose-h2:pb-2
            prose-h3:text-2xl prose-h3:mb-2 prose-h3:mt-5
            prose-p:text-white/80 prose-p:leading-relaxed prose-p:mb-4
            prose-a:text-[#00d4ff] prose-a:no-underline hover:prose-a:underline
            prose-code:text-[#00d4ff] prose-code:bg-white/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
            prose-pre:bg-black/50 prose-pre:border prose-pre:border-white/10 prose-pre:p-4
            prose-blockquote:border-l-4 prose-blockquote:border-[#00d4ff] prose-blockquote:text-white/70 prose-blockquote:pl-4 prose-blockquote:italic
            prose-strong:text-white prose-strong:font-semibold
            prose-ul:text-white/80 prose-ul:my-4 prose-ul:list-disc
            prose-ol:text-white/80 prose-ol:my-4 prose-ol:list-decimal
            prose-li:text-white/80 prose-li:mb-2
            prose-table:text-white/80 prose-table:border-collapse
            prose-th:border prose-th:border-white/20 prose-th:bg-white/5 prose-th:p-2
            prose-td:border prose-td:border-white/20 prose-td:p-2
            prose-hr:border-white/10 prose-hr:my-8"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
          </article>
        )}

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <h3 className="text-xl font-bold mb-4">More from Cortex</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(documents)
              .filter(([key]) => key !== docType)
              .map(([key, meta]) => (
                <Link
                  key={key}
                  to={`/cortex/${key}`}
                  className="p-4 bg-white/[0.02] border border-white/10 rounded-lg hover:bg-white/[0.05] hover:border-[#00d4ff]/30 transition-all"
                >
                  <div className="font-semibold text-white">{meta.title}</div>
                  <div className="text-sm text-white/60 mt-1">{meta.description}</div>
                </Link>
              ))}
            <Link
              to="/cortex"
              className="p-4 bg-white/[0.02] border border-white/10 rounded-lg hover:bg-white/[0.05] hover:border-[#00d4ff]/30 transition-all"
            >
              <div className="font-semibold text-white">Interactive Demo</div>
              <div className="text-sm text-white/60 mt-1">Try Cortex in your browser</div>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};
